import { Copy, FileText } from "lucide-react";
import SuccessCheckmark from "./SuccessCheckmark";
import useCopyToClipboard from "../hooks/useCopyToClipboard";
import useStructuredData from "../hooks/useStructuredData";
import { GSTIN_RECORD } from "../data/legalRegistrations";

export default function GstinBlock() {
  const { copy, copied } = useCopyToClipboard();
  const record = GSTIN_RECORD;

  useStructuredData(
    record
      ? {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: record.legalEntityName,
          taxID: record.gstin,
          address: { "@type": "PostalAddress", addressRegion: record.stateOfRegistration },
        }
      : null
  );

  if (!record) {
    return (
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5">
        <h3 className="font-display font-semibold text-ocean-950">GST Registration</h3>
        <p className="mt-2 text-sm text-ocean-800/60">
          GSTIN details will be published here once registration is complete.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5">
      <h3 className="font-display font-semibold text-ocean-950">GST Registration</h3>
      <dl className="mt-3 space-y-3 text-sm">
        <div>
          <dt className="text-xs uppercase tracking-wide text-ocean-800/50">GSTIN</dt>
          <dd className="mt-0.5 flex items-center gap-2 font-mono text-ocean-950">
            {record.gstin}
            <button
              type="button"
              onClick={() => copy(record.gstin)}
              aria-label="Copy GSTIN"
              className="text-ocean-800/40 hover:text-teal-600"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-ocean-800/50">Legal Entity Name</dt>
          <dd className="mt-0.5 text-ocean-950">{record.legalEntityName}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-ocean-800/50">State of Registration</dt>
          <dd className="mt-0.5 text-ocean-950">{record.stateOfRegistration}</dd>
        </div>
      </dl>

      {record.certificateUrl && (
        <a
          href={record.certificateUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600 hover:text-teal-700"
        >
          <FileText className="h-4 w-4" />
          View Certificate
        </a>
      )}

      <div
        role="status"
        aria-live="polite"
        className={`mt-3 flex items-center gap-1 text-xs font-medium text-teal-600 transition-opacity ${copied ? "opacity-100" : "opacity-0"}`}
      >
        {copied && (
          <>
            <SuccessCheckmark className="h-3 w-3" /> Copied to clipboard
          </>
        )}
      </div>
    </div>
  );
}
